import React, { PureComponent } from 'react';

import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Divider from '@material-ui/core/Divider';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import List from '@material-ui/core/List';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import classnames from 'classnames';
import { compact, debounce, filter, includes, map } from 'lodash';

import { EntitiesObject } from '@lib/resources/createResourceSelectors';
import { Entity } from '@lib/resources/types';

import FormattedSimpleMessage from '../FormattedSimpleMessage';
import TextFieldSearch from '../TextFieldSearch';

import FilterListItem, { FilterListItemProps } from './FilterListItem';
import messages from './messages';

const styles = ({ spacing: { unit }, breakpoints }: Theme) =>
  createStyles({
    root: {
      flex: 1,
    },
    listWrapper: {
      [breakpoints.up('md')]: {
        maxHeight: '350px',
        overflow: 'auto',
      },
    },
    formControlLabelPlacementStart: {
      flexDirection: 'row',
      marginLeft: 0,
    },
    divider: {
      margin: `0 ${unit * 1.5}px`,
    },
    formControlLabel: {
      fontWeight: 500,
    },
    checkbox: {
      padding: unit * 1.5,
    },
    searchBar: {
      margin: `${unit}px ${unit * 1.5}px 0 ${unit * 1.5}px`,
      width: `calc(100% - ${unit * 3}px)`,
    },
    displayLabel: {
      margin: `${unit}px ${unit * 1.5}px 0 ${unit * 1.5}px`,
    },
    marginTop: {
      marginTop: unit,
    },
  });

export interface FilterListProps extends WithStyles<typeof styles> {
  allItems: EntitiesObject<Entity>;
  allSelected?: boolean;
  disabled?: boolean;
  hideSearchField?: boolean;
  idsExcluded: string[];
  isGoogleFilter?: boolean;
  items: Entity[];
  itemsLabel?: string;
  limit?: number;
  loading?: boolean;
  onAllSelected?: (allSelected: boolean) => void;
  onClearItemsSelected: () => void;
  onItemSelected: FilterListItemProps['onItemSelected'];
  onSearchChange?: (page: number, limit: number, value?: string | undefined) => void;
  skipAllSelectedItemsDisabled?: boolean;
  skipSwitch?: boolean;
}

class FilterList extends PureComponent<FilterListProps> {
  state = {
    value: '',
  };

  componentDidMount() {
    this.handleSearchChange();
  }

  handleAllSelected = (event: any) => {
    const { onAllSelected } = this.props;
    const allSelected = event.target.checked;

    if (onAllSelected) onAllSelected(allSelected);

    // on selecting all should reset the list
    if (allSelected) {
      this.handleSearchChange();
    }
  };

  handleSearchDebounced = debounce((value) => {
    const { onSearchChange, limit = 8 } = this.props;
    if (onSearchChange) onSearchChange(1, limit, value || undefined);
  }, 500);

  handleSearchChange = (value = '') => {
    const { onSearchChange } = this.props;
    if (!onSearchChange) {
      return;
    }
    this.setState({ value });
    this.handleSearchDebounced(value);
  };

  render() {
    const {
      allItems,
      allSelected,
      classes,
      disabled,
      hideSearchField,
      idsExcluded,
      isGoogleFilter,
      items,
      itemsLabel,
      limit,
      loading,
      onAllSelected,
      onClearItemsSelected,
      onItemSelected,
      onSearchChange,
      skipAllSelectedItemsDisabled,
      skipSwitch,
      ...other
    } = this.props;
    const { value } = this.state;

    // Items excluded:
    // - Selected items in case of toggle: none
    // - Unselected items in case of toggle: all
    const itemsExcluded = compact(map(idsExcluded, (id) => allItems[id]));
    const itemsNotExcluded = compact(filter(items, ({ id }) => !includes(idsExcluded, id)));
    const allLabel = skipAllSelectedItemsDisabled ? 'Select all' : 'All';
    const showSearch = !hideSearchField && Boolean(onSearchChange) && !allSelected;
    const currentTotalItems = itemsNotExcluded.length + itemsExcluded.length;

    return (
      <div className={classes.root}>
        {!skipSwitch && (
          <React.Fragment>
            <FormControlLabel
              classes={{
                // @ts-ignore
                labelPlacementStart: classes.formControlLabelPlacementStart,
                label: classes.formControlLabel,
              }}
              disabled={!!disabled}
              control={
                <Checkbox
                  classes={{
                    root: classes.checkbox,
                  }}
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  checked={allSelected}
                  disabled={!!disabled}
                  onChange={this.handleAllSelected}
                  color="primary"
                  tabIndex={-1}
                  disableRipple
                />
              }
              label={allLabel}
              labelPlacement="start"
            />
            <Divider className={classes.divider} />
          </React.Fragment>
        )}
        {showSearch && !disabled && (
          <TextFieldSearch
            className={classes.searchBar}
            onChange={this.handleSearchChange}
            placeholder="Search"
            size="small"
            value={value}
          />
        )}
        {itemsExcluded.length > 0 && !disabled && !isGoogleFilter && (
          <Button className={classes.marginTop} size="small" fullWidth onClick={onClearItemsSelected}>
            <FormattedSimpleMessage {...(allSelected ? messages.clearExcluded : messages.clearSelections)} />
          </Button>
        )}
        <div>
          {/* Items excluded */}
          {itemsExcluded.length > 0 && (
            <List className={classes.listWrapper}>
              {map(itemsExcluded, (item) => (
                <FilterListItem
                  key={item.id}
                  item={item}
                  selectedIds={idsExcluded}
                  checked={!allSelected}
                  onItemSelected={onItemSelected}
                  disabled={disabled || isGoogleFilter}
                  {...other}
                />
              ))}
            </List>
          )}
          {itemsExcluded.length > 0 && itemsNotExcluded.length > 0 && <Divider />}
          {/* Items not excluded */}
          {!loading && itemsNotExcluded.length > 0 && (
            <List className={classes.listWrapper}>
              {map(itemsNotExcluded, (item) => (
                <FilterListItem
                  key={item.id}
                  item={item}
                  checked={allSelected}
                  onItemSelected={onItemSelected}
                  disabled={(!skipAllSelectedItemsDisabled && allSelected) || !!disabled}
                  {...other}
                />
              ))}
            </List>
          )}
        </div>
        {itemsLabel && showSearch && !disabled && (
          <Typography
            className={classnames(
              {
                [classes.marginTop]: itemsNotExcluded.length === 0,
              },
              classes.displayLabel,
            )}
            variant="caption"
            gutterBottom
          >
            {`${currentTotalItems > 0 ? `Displaying ${currentTotalItems} ${itemsLabel}. ` : ''}${
              allSelected ? '' : `Please use search to see other ${itemsLabel}.`
            }`}
          </Typography>
        )}
      </div>
    );
  }
}

export default withStyles(styles)(FilterList);
